import { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
   Button,
   Div,
   filterHover,
   Form,
   InputField,
   PageHeader,
   Text,
   useForm,
   useLoader,
   useTheme,
   type PartialRecord,
} from "react-better-html";
import { useNavigate } from "react-router-dom";

import { company } from "../../constants/company";

import { organizationTypes, type OrganizationType } from "../../types/organization";

import { useReduxAction, useReduxState } from "../../utils/hooks";
import { emailIsValid } from "../../utils/functions";
import { useI18n } from "../../utils/i18n";

import AppLogo from "../../components/AppLogo";
import CenterContentPageHolder, {
   type CenterContentPageHolderComponentDecorationProps,
} from "../../components/structure/CenterContentPageHolder";

type StepData = {
   title?: string;
   description?: string;
   decorationVariant?: CenterContentPageHolderComponentDecorationProps["variant"];
};

type EmailInvite = {
   email: string;
   isValid?: boolean;
};

const maxStepsPerOrganizationType: Record<OrganizationType, number> = {
   PERSONAL: 2,
   AGENCY: 3,
};

const initialEmailInvite: EmailInvite = {
   email: "",
};

function CreateOrganization() {
   const theme = useTheme();
   const { t } = useI18n();
   const navigate = useNavigate();
   const createOrganizationIsLoading = useLoader("organization:createOrganization");

   const firebaseUser = useReduxState((state) => state.user.firebaseUser);
   const organizationsList = useReduxState((state) => state.organizations.list);

   const createOrganization = useReduxAction((actions) => actions.organizations.createOrganization);

   const [step, setStep] = useState<number>(1);
   const [selectedOrganizationType, setSelectedOrganizationType] = useState<OrganizationType>("PERSONAL");
   const [enteredEmailsForInvite, setEnteredEmailsForInvite] = useState<EmailInvite[]>([
      initialEmailInvite,
      initialEmailInvite,
      initialEmailInvite,
   ]);

   const form = useForm({
      defaultValues: {
         name: "",
      },
      requiredFields: ["name"],
      onSubmit: (values) => {
         createOrganization({
            data: {
               name: values.name,
               type: selectedOrganizationType,
            },
            onSuccess: () => {
               navigate("/");
            },
         });
      },
   });

   const onClickNextStep = useCallback(() => {
      const newStep = step + 1;

      let canContinue = true;

      if (newStep === 3) {
         if (selectedOrganizationType === "AGENCY") {
            if (form.values.name.trim() === "") {
               form.inputFieldRefs.name?.focus();
               canContinue = false;
            }
         }
      }

      if (canContinue) setStep(newStep);
   }, [step, selectedOrganizationType, form.values.name]);
   const onClickPreviousStep = useCallback(() => {
      setStep((oldValue) => oldValue - 1);
   }, []);
   const onClickSkip = useCallback(() => {
      form.onSubmit();
   }, [form]);
   const onClickCancel = useCallback(() => {
      navigate("/");
   }, []);

   const stepData = useMemo<Record<number, StepData & PartialRecord<OrganizationType, StepData>>>(
      () => ({
         1: {
            title: `${t((l) => l.pages.organization.create.steps[1].title)} ${company.name}?`,
            description: t((l) => l.pages.organization.create.steps[1].description),
            AGENCY: {
               decorationVariant: "option2",
            },
         },
         2: {
            description: t((l) => l.pages.organization.create.steps[2].description),
            decorationVariant: "organizationName",
            PERSONAL: {
               title: t((l) => l.pages.organization.create.steps[2].titlePersonal),
            },
            AGENCY: {
               title: t((l) => l.pages.organization.create.steps[2].title),
            },
         },
         3: {
            title: t((l) => l.pages.organization.create.steps[3].title),
            description: t((l) => l.pages.organization.create.steps[3].description),
            decorationVariant: "organizationMembers",
         },
      }),
      [t],
   );
   const members = useMemo<string[]>(
      () => [
         firebaseUser?.displayName ?? "",
         ...enteredEmailsForInvite.filter((email) => email.email.trim() !== "").map((email) => email.email),
      ],
      [firebaseUser, enteredEmailsForInvite],
   );

   useEffect(() => {
      if (selectedOrganizationType === "PERSONAL") {
         form.setFieldValue(
            "name",
            firebaseUser ? `${(firebaseUser.displayName ?? "").split(" ")[0]}'s Team` : "My Team",
         );
      } else if (selectedOrganizationType === "AGENCY") {
         form.setFieldValue("name", "");
      }
   }, [selectedOrganizationType, firebaseUser]);
   useEffect(() => {
      const filledEmails = enteredEmailsForInvite.filter((item) => item.email.trim() !== "");

      if (filledEmails.length >= 3) {
         if (filledEmails.length === enteredEmailsForInvite.length) {
            setEnteredEmailsForInvite((oldValue) => [...oldValue, initialEmailInvite]);
         }
      }

      if (enteredEmailsForInvite.length > 3 && filledEmails.length === enteredEmailsForInvite.length - 2) {
         setEnteredEmailsForInvite((oldValue) => oldValue.slice(0, -1));
      }
   }, [enteredEmailsForInvite]);

   const onLastStep = step === maxStepsPerOrganizationType[selectedOrganizationType];

   return (
      <>
         <CenterContentPageHolder.content>
            <Div.row alignItems="center" justifyContent="space-between" marginBottom={theme.styles.space * 2}>
               <AppLogo />

               <Text fontSize={12} color={theme.colors.textSecondary}>
                  {t((l) => l.common.step)} {step} {t((l) => l.common.of)}{" "}
                  {maxStepsPerOrganizationType[selectedOrganizationType]}
               </Text>
            </Div.row>

            <PageHeader
               title={stepData[step][selectedOrganizationType]?.title ?? stepData[step].title}
               description={stepData[step][selectedOrganizationType]?.description ?? stepData[step].description}
            />

            <Form form={form} gap={theme.styles.gap}>
               {step === 1 ? (
                  <>
                     {organizationTypes.map((organizationType) => (
                        <Div.box
                           isActive={selectedOrganizationType === organizationType}
                           backgroundColor={
                              selectedOrganizationType === organizationType ? theme.colors.primary + "20" : undefined
                           }
                           borderWidth={2}
                           borderRadius={theme.styles.borderRadius}
                           padding={theme.styles.space}
                           value={organizationType}
                           onClickWithValue={setSelectedOrganizationType}
                           key={organizationType}
                        >
                           <PageHeader
                              title={t((l) => l.pages.organization.create.steps[1].options[organizationType].title)}
                              titleAs="h4"
                              description={t(
                                 (l) => l.pages.organization.create.steps[1].options[organizationType].description,
                              )}
                              descriptionFontSize={14}
                              marginBottom={0}
                           />
                        </Div.box>
                     ))}
                  </>
               ) : step === 2 ? (
                  <InputField
                     label={t(
                        (l) =>
                           l.pages.organization.create.steps[2][
                              selectedOrganizationType === "PERSONAL" ? "namePersonal" : "name"
                           ],
                     )}
                     autoFocus
                     autoComplete="off"
                     backgroundColor={theme.colors.backgroundBase}
                     {...form.getInputFieldProps("name")}
                  />
               ) : step === 3 ? (
                  <>
                     {enteredEmailsForInvite.map(({ email, isValid }, index) => (
                        <InputField.email
                           placeholder="colleague@agency.com"
                           backgroundColor={theme.colors.backgroundBase}
                           autoFocus={index === 0}
                           errorText={
                              isValid === false ? t((l) => l.pages.organization.create.steps[3].emailError) : undefined
                           }
                           marginBottom={isValid === false ? theme.styles.gap : undefined}
                           value={email}
                           onChangeValue={(value) =>
                              setEnteredEmailsForInvite((oldValue) =>
                                 oldValue.map((item, itemIndex) =>
                                    itemIndex === index
                                       ? {
                                            email: value,
                                         }
                                       : item,
                                 ),
                              )
                           }
                           onBlur={() => {
                              if (email && !emailIsValid(email)) {
                                 setEnteredEmailsForInvite((oldValue) =>
                                    oldValue.map((item, itemIndex) =>
                                       itemIndex === index
                                          ? {
                                               ...item,
                                               isValid: false,
                                            }
                                          : item,
                                    ),
                                 );
                              }
                           }}
                           key={index}
                        />
                     ))}
                  </>
               ) : undefined}

               <Div.row alignItems="center" gap={theme.styles.gap} marginTop={theme.styles.space * 2}>
                  {step === 1 && (organizationsList?.length ?? 0) > 0 && (
                     <Button.secondary
                        text={t((l) => l.common.cancel)}
                        backgroundColor={theme.colors.backgroundBase}
                        onClick={onClickCancel}
                     />
                  )}
                  {step > 1 && (
                     <Button.secondary
                        text={t((l) => l.common.back)}
                        backgroundColor={theme.colors.backgroundBase}
                        onClick={onClickPreviousStep}
                     />
                  )}

                  <Button
                     flex={1}
                     text={
                        onLastStep
                           ? t((l) => l.pages.organization.create.submitButton[selectedOrganizationType])
                           : t((l) => l.common.continue)
                     }
                     type={onLastStep ? "submit" : undefined}
                     loaderName="organization:createOrganization"
                     backgroundColor={theme.colors.textPrimary}
                     onClick={!onLastStep ? onClickNextStep : undefined}
                     key={step}
                  />
               </Div.row>

               {step === 3 && (
                  <Div.row alignItems="center" justifyContent="center">
                     <Text
                        fontSize={14}
                        textAlign="center"
                        color={theme.colors.textSecondary}
                        filterHover={filterHover().z2}
                        marginTop={theme.styles.gap}
                        padding={theme.styles.gap}
                        opacity={createOrganizationIsLoading ? 0.6 : undefined}
                        transition={theme.styles.transition}
                        cursor="pointer"
                        onClick={!createOrganizationIsLoading ? onClickSkip : undefined}
                     >
                        {t((l) => l.pages.organization.create.skip)}
                     </Text>
                  </Div.row>
               )}
            </Form>
         </CenterContentPageHolder.content>

         <CenterContentPageHolder.decoration
            variant={stepData[step][selectedOrganizationType]?.decorationVariant ?? stepData[step]?.decorationVariant}
            organizationType={selectedOrganizationType}
            organizationName={form.values.name}
            members={members}
            marketingText={
               selectedOrganizationType === "PERSONAL"
                  ? t((l) => l.pages.auth.marketingText)
                  : selectedOrganizationType === "AGENCY"
                    ? t((l) => l.pages.organization.create.marketingText)
                    : undefined
            }
         />
      </>
   );
}

export default memo(CreateOrganization);
